import { useEffect, useContext, useState, useCallback } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import firebaseMessagingService from '../services/firebaseMessaging';

const useFirebaseNotifications = () => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const { state } = useContext(AppContext);
    const [permissionStatus, setPermissionStatus] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
  
    const handleNotificationClick = useCallback((data) => {
        console.log('[Notification] Click data received:', data);
        
        if (!data) {
            console.warn('[Notification] No data provided, navigating to notifications');
            navigate('/notifications');
            return;
        }
    
        // Convert type to string to handle both number and string types
        const screenType = String(data.type || data.notificationType || '');
        
        const dataId = data.data_id || data.dataId || data.id;
        
        console.log('[Notification] Screen type:', screenType, 'Data ID:', dataId);
            
        switch (screenType) {
            case '1': // Appointment
                if (dataId) {
                    navigate(`/appointments/${dataId}`);
                } else {
                    navigate('/appointments');
                }
                break;
      
            case '2': // Side Effect/Health Check
                if (dataId) {
                    navigate(`/follow-ups/questionnaire/${dataId}`);
                } else {
                    navigate('/follow-ups');
                }
                break;
      
            case '4': // Message/Chat
                navigate('/messages');
                break;
      
            case '6': // Prescription/Shopping
                navigate('/prescriptions');
                break;
      
            case '7':
                if (dataId) {
                    navigate(`/patients/${dataId}`);
                } else {
                    navigate('/patients');
                }
                break;
      
            case '8': // Home/Dashboard
                navigate('/');
                break;
      
            case '10': // Video Call - Redirect to Appointment Details
                if (dataId) {
                    navigate(`/appointments/${dataId}`);
                } else {
                    navigate('/appointments');
                }
                break;
      
            default:
                navigate('/notifications');
                break;
        }
        
        // Dispatch event to refresh notification count in Header after navigation
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('notification-clicked'));
        }, 1000);
    }, [navigate]);

    const handleForegroundMessage = useCallback(async (payload) => {
        await firebaseMessagingService.showNativeNotification(payload);
        
        window.dispatchEvent(new CustomEvent('notification-received'));
    }, []);

    const handleServiceWorkerMessage = useCallback((event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
            const notificationData = event.data.data;
            handleNotificationClick(notificationData);
        }
    }, [handleNotificationClick]);

    const initializeNotifications = useCallback(async () => {
        try {
            if (!firebaseMessagingService.isNotificationSupported()) {
                console.warn('Notifications not supported in this browser');
                setIsInitialized(true);
                return;
            }

            // Register service worker first
            await firebaseMessagingService.registerServiceWorker();

            // Check permission status
            const permission = firebaseMessagingService.getPermissionStatus();
            setPermissionStatus(permission);

            if (permission === 'granted') {
                // Get FCM token
                const token = await firebaseMessagingService.getFCMToken();
            
                if (token) {
                    // Register device with backend
                    await firebaseMessagingService.registerDevice();
                }

                // Listen for foreground messages
                firebaseMessagingService.onMessage(handleForegroundMessage);
            
                // Listen for service worker messages (notification clicks)
                if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
                }
            } else if (permission === 'default') {
                // Permission not yet requested - automatically request it
                const token = await firebaseMessagingService.requestPermission();
                
                if (token) {
                    // Register device after permission granted
                    await firebaseMessagingService.registerDevice();
                    setPermissionStatus('granted');
                    
                    // Listen for foreground messages
                    firebaseMessagingService.onMessage(handleForegroundMessage);
                    
                    // Listen for service worker messages (notification clicks)
                    if ('serviceWorker' in navigator) {
                        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
                    }
                } else {
                    setPermissionStatus('denied');
                }
            } else if (permission === 'denied') {
                console.warn('Notification permission denied by user');
            }

            setIsInitialized(true);
        } catch (error) {
            console.error('Error initializing notifications:', error);
            setIsInitialized(true);
        }
    }, [handleForegroundMessage, handleServiceWorkerMessage]);

    useEffect(() => {
        if (state?.currentUser?.id) {
            initializeNotifications();
        }

        return () => {
            // Cleanup if needed
        };
    }, [state?.currentUser?.id, initializeNotifications]);

    const requestPermission = async () => {
        try {
            const token = await firebaseMessagingService.requestPermission();
          
            if (token) {
                // Register device after permission granted
                await firebaseMessagingService.registerDevice();
                message.success('Notifications enabled successfully!');
                setPermissionStatus('granted');
            } else {
                message.warning('Notification permission denied');
                setPermissionStatus('denied');
            }
        } catch (error) {
            console.error('Error requesting permission:', error);
            message.error('Failed to enable notifications');
        }
    };

    return {
        permissionStatus,
        isInitialized,
        requestPermission,
        isSupported: firebaseMessagingService.isNotificationSupported()
    };
};

export default useFirebaseNotifications;